import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { formatDistance } from 'date-fns';
import { css, Theme } from '@emotion/react';
import { Trash, CaretDown, CrownSimple, Plus, Envelope } from '@phosphor-icons/react';
import type { ChangeRoleResponse } from 'api/account/handlers/changeRoleHandler';
import type { DeleteAccountInviteResponse } from 'api/account/handlers/deleteAccountInviteHandler';
import type { RemoveFromAccountResponse } from 'api/account/handlers/removeFromAccountHandler';
import { AccessRights } from 'common/account/AccessRights';
import type { ChangeRoleRequest } from 'common/account/ChangeRoleDocuments';
import type { DeleteAccountInviteRequest } from 'common/account/DeleteAccountInviteDocuments';
import type { RemoveFromAccountRequest } from 'common/account/RemoveFromAccountDocuments';
import { AccountUserRole } from 'common/CommonConstants';
import Api from 'client/lib/Api';
import { useEditAppContext } from 'client/lib/EditApp';
import { useError } from 'client/lib/Error';
import { useUser } from 'client/lib/User';
import { useAccountState } from 'client/state/AccountState';
import { Button } from 'client/components/Button';
import { Catalog, CatalogItemProps } from 'client/components/Catalog';
import { Menu } from 'client/components/Menu/Menu';
import { MenuItemProps } from 'client/components/Menu/MenuItem';
import { Tag } from 'client/components/Tag';
import { InviteUserDialog } from './InviteUsersDialog';
import { UserInviteBlockDialog } from './UserInviteBlockDialog';

const roleButtonStyle = (theme: Theme) => css`
  > .icon > svg {
    transform: translate(-3px, 1px);
    color: ${theme.color.grey[100]};
  }
`;

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const catalogStyle = css`
  a {
    color: inherit;
  }
`;

const menuStyle = css`
  .MuiList-root {
    width: 260px;
  }

  .MuiAvatar-img {
    padding: 4px;
  }
`;

const adminTagStyle = (theme: Theme) => css`
  transform: translateY(2px);
  color: ${theme.color.gold} !important;
`;

const deleteInvite = ({ accountID, email }: DeleteAccountInviteRequest) =>
  new Promise<DeleteAccountInviteResponse>((resolve, reject) =>
    Api.post<DeleteAccountInviteRequest, DeleteAccountInviteResponse>('/account/invite/delete', { accountID, email })
      .then((response) => resolve(response))
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

const removeUser = ({ accountID, userID }: RemoveFromAccountRequest) =>
  new Promise<RemoveFromAccountResponse>((resolve, reject) =>
    Api.post<RemoveFromAccountRequest, RemoveFromAccountResponse>('/account/user/remove', { accountID, userID })
      .then((response) => resolve(response))
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

const changeUserRole = ({ accountID, userID, accountUserRoleID }: ChangeRoleRequest) =>
  new Promise<ChangeRoleResponse>((resolve, reject) =>
    Api.post<ChangeRoleRequest, ChangeRoleResponse>('/account/user/role', { accountID, userID, accountUserRoleID })
      .then((response) => resolve(response))
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

const adminTag = (
  <>
    <CrownSimple weight="fill" css={adminTagStyle} /> Admin
  </>
);

const memberTag = 'Member';

const roleMenuItems: MenuItemProps[] = [
  {
    id: `${AccountUserRole.ADMINISTRATOR}`,
    title: adminTag,
  },
  {
    id: `${AccountUserRole.MEMBER}`,
    title: memberTag,
  },
];

export const UserList = () => {
  const accountID = useAccountState((state) => state.document?.id || 0);
  const users = useAccountState((state) => state.document?.users || []);
  const invites = useAccountState((state) => state.document?.invites || []);
  const { user: currentUser, updateSession, hasAccessRight } = useUser();
  const editApp = useEditAppContext();
  const [invite, setInvite] = useState(false);
  const { setError } = useError();
  const [roleMenu, setRoleMenu] = useState<{ userID: number; anchrolEl: HTMLElement } | null>(null);

  // FIXME: Do not allow "trial" users to invite new users
  const isTrial = hasAccessRight(AccessRights.TRIAL_USER);

  const editAppContext = useEditAppContext();

  const deleteInviteMutation = useMutation({
    mutationFn: deleteInvite,
  });

  const removeUserMutation = useMutation({
    mutationFn: removeUser,
  });

  const changeUserRoleMutation = useMutation({
    mutationFn: changeUserRole,
  });

  const currentUserID = currentUser?.userID;

  const handleDelete = useCallback(
    async (email: string) => {
      setError(null);
      const { status } = await deleteInviteMutation.mutateAsync({ accountID, email });
      if (status !== 'ok') {
        setError('Failed to remove invite. Please try again.', { status });
        return;
      }
      editAppContext.load();
    },
    [accountID, editAppContext.load]
  );

  const handleRemoveUser = useCallback(
    async (userID: number) => {
      setError(null);
      const { status } = await removeUserMutation.mutateAsync({ accountID, userID });
      if (status !== 'ok') {
        setError('Failed to remove user. Please try again.', { status });
        return;
      }
      editAppContext.load();
    },
    [accountID, editAppContext.load]
  );

  const handleChangeUserRole = async (userID: number, accountUserRoleID: AccountUserRole) => {
    setError(null);
    const { status } = await changeUserRoleMutation.mutateAsync({ accountID, userID, accountUserRoleID });
    if (status !== 'ok') {
      setError('Failed to change user role. Please try again.', { status });
      return;
    }
    editAppContext.load();
    await updateSession();
    setRoleMenu(null);
  };

  const usersItems = useMemo<CatalogItemProps[]>(
    () =>
      users.map((user) => ({
        id: `${user.userID}`,
        title: (
          <>
            {currentUserID === user.userID && <Tag>You</Tag>}
            {[user.firstName, user.lastName].filter((item) => !!item).join(' ')}
          </>
        ),
        subtitle: (
          <>
            {user.githubUsername && (
              <a href={`https://github.com/${user.githubUsername}`} target="_blank">
                @{user.githubUsername}
              </a>
            )}
            {' · '}
            {user.accountUserRoleID === AccountUserRole.ADMINISTRATOR ? adminTag : memberTag}
          </>
        ),
        image: { alt: 'profile picture', src: `https://github.com/${user.githubUsername}.png?size=32`, shape: 'circle' },
        action: (
          <>
            <Button
              icon={<CaretDown weight="fill" />}
              small
              css={roleButtonStyle}
              onClick={(event) => setRoleMenu({ userID: user.userID, anchrolEl: event.currentTarget })}
            >
              {user.accountUserRoleID === AccountUserRole.ADMINISTRATOR ? adminTag : memberTag}
            </Button>
            {currentUserID !== user.userID && (
              <Button icon={<Trash />} small danger onClick={() => handleRemoveUser(user.userID)}>
                Remove
              </Button>
            )}
          </>
        ),
      })),
    [users, currentUserID, handleRemoveUser]
  );

  const invitesItems = useMemo(
    () =>
      invites.map((invite) => ({
        id: `${invite.email}`,
        title: invite.email,
        subtitle: <>Invited, invite expires in {formatDistance(new Date(invite.timeExpires), new Date())}</>,
        icon: <Envelope />,
        action: (
          <>
            <Button icon={<Trash />} small danger disabled={deleteInviteMutation.isLoading} onClick={() => handleDelete(invite.email)}>
              Remove
            </Button>
          </>
        ),
      })),
    [users, handleDelete, deleteInviteMutation.isLoading]
  );

  return (
    <>
      <Catalog items={[...usersItems, ...invitesItems]} css={catalogStyle} />
      <div css={actionsStyle}>
        <Button icon={<Plus weight="bold" />} onClick={() => setInvite(true)}>
          Invite Users
        </Button>
      </div>

      {!isTrial && (
        <InviteUserDialog
          open={invite}
          accountID={accountID}
          onClose={() => {
            setInvite(false);
            editApp.load();
          }}
        />
      )}
      {isTrial && (
        <UserInviteBlockDialog
          open={invite}
          onClose={() => {
            setInvite(false);
          }}
        />
      )}

      <Menu
        items={roleMenuItems}
        anchorEl={roleMenu?.anchrolEl || null}
        open={!!roleMenu?.anchrolEl}
        onClose={() => setRoleMenu(null)}
        onSelect={(role) => {
          if (!roleMenu) return;
          handleChangeUserRole(roleMenu.userID, parseInt(role) as AccountUserRole);
        }}
        css={menuStyle}
      />
    </>
  );
};
